import React from 'react';
import { Box, styled } from '@mui/material';
import { IPublicAndPrivateSettings } from '@pepperhq/location-sdk';

import logger from 'lib/logger';
import { parseIntOrUndefined } from 'lib/helpers';
import { SettingDisplay } from 'lib/SettingDisplay';
import { MuiCardForm } from 'lib/form/MuiCardForm';
import { NumberFormField } from 'lib/form/fields/NumberFormField';
import { SwitchFormField } from 'lib/form/fields/SwitchFormField';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { getPublicAndPrivateSettings } from 'store/settings/settingsSlice';
import { DEFAULT_MAX_USER_METRICS } from 'components/metrics/model/loyalty-tier';
import { LocationSettingsApi } from 'components/location/LocationSettingsApi';
import { ILocationScopedSettingsComponentProps } from '../LocationScopedSettings';
import { SettingDisplayText } from '../SettingDisplayText';
import { SettingDisplaySwitch } from '../SettingDisplaySwitch';

const PREFIX = 'InternalSettings';

const classes = {
    settingsCard: `${PREFIX}-settingsCard`,
    displayClassName: `${PREFIX}-displayClassName`
};

const StyledMuiCardForm = styled(MuiCardForm)(({ theme }) => ({
    [`&.${classes.settingsCard}`]: {
        marginBottom: theme.spacing(2),
        overflowY: 'visible' // Needed for the dropdowns
    },
    [`& .${classes.displayClassName}`]: {
        padding: theme.spacing(2)
    }
}));

export const InternalSettingsCard: React.FC<ILocationScopedSettingsComponentProps> = ({
    settings,
    onSettingsSaved,
    locationId
}) => {
    const { claims } = useAppSelector(state => state.auth);
    const dispatch = useAppDispatch();

    const handleSubmit = React.useCallback(
        async (values: Record<string, any>) => {
            try {
                let settingsToUpdate: IPublicAndPrivateSettings = {};

                // Internal settings are global only
                if (locationId === undefined) {
                    settingsToUpdate = {
                        premiumLoyaltyEnabled: values.premiumLoyaltyEnabled,
                        premiumLoyalty: {
                            maximumNumberOfUserMetrics:
                                parseIntOrUndefined(values.maximumNumberOfUserMetrics) ??
                                DEFAULT_MAX_USER_METRICS
                        },
                        scheduledNotificationsEnabled: values.scheduledNotificationsEnabled,
                        triggeredNotificationsEnabled: values.triggeredNotificationsEnabled,
                        links: {
                            utmParametersEnabled: values.advancedTrackingEnabled,
                            unlimitedAppLinksEnabled: values.advancedTrackingEnabled
                        },
                        subscriptionsEnabled: values.subscriptionsEnabled
                    };
                }

                if (Object.keys(settingsToUpdate).length === 0) {
                    // No need to update nothing
                    return true;
                }
                await new LocationSettingsApi().updatePublicAndPrivateSettings(settingsToUpdate, locationId);
                dispatch(getPublicAndPrivateSettings(claims));

                await onSettingsSaved();
                return true;
            } catch (err) {
                logger.error(`Error Updating Settings: ${JSON.stringify(err)}`);
                return false;
            }
        },
        [claims, dispatch, locationId, onSettingsSaved]
    );

    return (
        <StyledMuiCardForm
            title="Internal Settings"
            className={classes.settingsCard}
            boxClassName={classes.displayClassName}
            initialValues={{
                premiumLoyaltyEnabled: settings?.premiumLoyaltyEnabled ?? false,
                maximumNumberOfUserMetrics:
                    settings?.premiumLoyalty?.maximumNumberOfUserMetrics ?? DEFAULT_MAX_USER_METRICS,
                scheduledNotificationsEnabled: settings?.scheduledNotificationsEnabled ?? false,
                triggeredNotificationsEnabled: settings?.triggeredNotificationsEnabled ?? false,
                advancedTrackingEnabled:
                    (settings?.links?.utmParametersEnabled ?? false) &&
                    (settings?.links?.unlimitedAppLinksEnabled ?? false),
                subscriptionsEnabled: settings?.subscriptionsEnabled ?? false
            }}
            onSubmit={handleSubmit}
            childrenWhileDisplaying={
                <Box display="flex" flexDirection="column">
                    <SettingDisplaySwitch
                        title="Premium Loyalty Enabled"
                        checked={settings?.premiumLoyaltyEnabled ?? false}
                    />
                    <SettingDisplayText
                        title="Number of User Metrics Enabled"
                        text={
                            settings?.premiumLoyalty?.maximumNumberOfUserMetrics ?? DEFAULT_MAX_USER_METRICS
                        }
                    />
                    <SettingDisplaySwitch
                        title="Scheduled Notifications Enabled"
                        checked={settings?.scheduledNotificationsEnabled ?? false}
                    />
                    <SettingDisplaySwitch
                        title="Triggered Notifications Enabled"
                        checked={settings?.triggeredNotificationsEnabled ?? false}
                    />
                    <SettingDisplaySwitch
                        title="Advanced Tracking Enabled"
                        description="Enables UTM tracking, table numbers, and unlimited campaign links."
                        checked={
                            (settings?.links?.utmParametersEnabled ?? false) &&
                            (settings?.links?.unlimitedAppLinksEnabled ?? false)
                        }
                    />
                    <SettingDisplaySwitch
                        title="Subscriptions Enabled"
                        checked={settings?.subscriptionsEnabled ?? false}
                    />
                </Box>
            }
            childrenWhileEditing={
                <Box display="flex" flexDirection="column">
                    <SettingDisplay label="Premium Loyalty Enabled">
                        <SwitchFormField name="premiumLoyaltyEnabled" />
                    </SettingDisplay>
                    <SettingDisplay label="Number of User Metrics Enabled">
                        <NumberFormField name="maximumNumberOfUserMetrics" />
                    </SettingDisplay>
                    <SettingDisplay label="Scheduled Notifications Enabled">
                        <SwitchFormField name="scheduledNotificationsEnabled" />
                    </SettingDisplay>
                    <SettingDisplay label="Triggered Notifications Enabled">
                        <SwitchFormField name="triggeredNotificationsEnabled" />
                    </SettingDisplay>
                    <SettingDisplay
                        label="Advanced Tracking Enabled"
                        description="Enables UTM tracking, table numbers, and unlimited campaign links."
                    >
                        <SwitchFormField name="advancedTrackingEnabled" />
                    </SettingDisplay>
                    <SettingDisplay label="Subscriptions Enabled">
                        <SwitchFormField name="subscriptionsEnabled" />
                    </SettingDisplay>
                </Box>
            }
        />
    );
};
